// Lightweight client-side reading history for "Continue Exploring" and personalization.
// Stored independently of the curiosity score so it can be cleared/expanded freely.

const KEY = "sa.history.v1";
const MAX = 24;

export type HistoryEntry = {
  slug: string;
  categoryId?: string | null;
  ts: number;
};

export function getHistory(): HistoryEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

export function recordHistory(slug: string, categoryId?: string | null) {
  if (typeof window === "undefined" || !slug) return;
  try {
    const cur = getHistory().filter((e) => e.slug !== slug);
    cur.unshift({ slug, categoryId: categoryId ?? null, ts: Date.now() });
    window.localStorage.setItem(KEY, JSON.stringify(cur.slice(0, MAX)));
  } catch {
    /* ignore */
  }
}

// Deterministic seed that flips twice per day in Europe/Berlin (CET/CEST):
// once at 06:00 and again at 18:00. SSR and client agree because the bucket
// is derived from the same wall-clock hour in the same timezone.
export function dailySeed(salt = 0): number {
  let y: number, m: number, day: number, hour: number;
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Europe/Berlin",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      hour12: false,
    }).formatToParts(new Date());
    y = Number(parts.find((p) => p.type === "year")?.value ?? 0);
    m = Number(parts.find((p) => p.type === "month")?.value ?? 0);
    day = Number(parts.find((p) => p.type === "day")?.value ?? 0);
    hour = Number(parts.find((p) => p.type === "hour")?.value ?? 0);
  } catch {
    const d = new Date();
    y = d.getUTCFullYear();
    m = d.getUTCMonth() + 1;
    day = d.getUTCDate();
    hour = d.getUTCHours();
  }
  // Two buckets per day: morning (06:00–17:59) = 0, evening (18:00–05:59) = 1.
  const half = hour >= 6 && hour < 18 ? 0 : 1;
  // If we're before 6am, treat as previous day's evening bucket so the seed
  // stays stable across midnight without flipping a third time.
  const dayKey = half === 1 && hour < 6 ? day - 1 : day;
  const n = y * 1000000 + m * 10000 + dayKey * 100 + half * 7 + salt;
  let h = n | 0;
  h = (h ^ (h >>> 16)) * 0x85ebca6b;
  h = (h ^ (h >>> 13)) * 0xc2b2ae35;
  h = h ^ (h >>> 16);
  return Math.abs(h | 0);
}

// Fast-rotating seed for homepage rails. Buckets every 15 minutes so the
// hero / reels / tiles cycle through the full catalogue across a normal
// browsing day. SSR + first client render agree because the bucket is
// derived from the same wall-clock minute.
export function rotatingSeed(salt = 0, bucketMinutes = 15): number {
  const now = Date.now();
  const bucket = Math.floor(now / (bucketMinutes * 60 * 1000));
  let h = (bucket + salt) | 0;
  h = (h ^ (h >>> 16)) * 0x85ebca6b;
  h = (h ^ (h >>> 13)) * 0xc2b2ae35;
  h = h ^ (h >>> 16);
  return Math.abs(h | 0);
}

export function todayLabel(): string {
  return new Date().toLocaleDateString(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
}
